import React from 'react';
import styled from '@emotion/styled';

import { useIsMobile } from '@hooks/responsive/useIsMobile';

import TextInput from '@components/_atoms/TextInput';
import Typography from '@components/_atoms/Typography';

import { TextInputAlign, TextInputVariant, TypoVariant } from '@constants/atoms';

import { primary, red } from '@styles/Colors';

type DaysInputBoxProps = {
  title?: string;
  textInput: TextInputType;
  errorMessage?: string;
  successMessage?: string;
  isRequired?: boolean;
  disabled?: boolean;
};

type TextInputType = {
  variant?: TextInputVariant;
  placeholder?: string;
  name?: string;
  value?: string | number;
  type?: string;
  align?: TextInputAlign;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEnter?: () => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  autoComplete?: string;
  suffixText?: string;
  style?: React.CSSProperties;
};

const DaysInputBoxContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
`;

const HighLightRed = styled.span`
  color: ${red.red3};
`;

const DaysInputBox = ({ title, textInput, isRequired, disabled }: DaysInputBoxProps) => {
  const isMobile = useIsMobile();
  const {
    name,
    value,
    type,
    align,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    placeholder,
    autoComplete,
    style,
    suffixText,
  } = textInput;

  const handleKeyDownTextInput = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      textInput?.onEnter?.();
    }
  };

  return (
    <DaysInputBoxContainer>
      {title && (
        <Typography variant={isMobile ? TypoVariant.SH4 : TypoVariant.SH3} color={primary.gray}>
          {title}
          {isRequired && <HighLightRed> *</HighLightRed>}
        </Typography>
      )}

      <InputContainer>
        <TextInput
          variant={disabled ? TextInputVariant.FIXED : TextInputVariant.DEFAULT}
          placeholder={placeholder}
          name={name}
          value={value}
          type="text"
          align={align}
          onChange={onChange}
          onKeyDown={onKeyDown ?? handleKeyDownTextInput}
          onFocus={onFocus}
          onBlur={onBlur}
          autoComplete={autoComplete}
          style={style}
        />

        {textInput.suffixText && <Typography variant={TypoVariant.SH4}>{suffixText}</Typography>}
      </InputContainer>
    </DaysInputBoxContainer>
  );
};

export default DaysInputBox;
